
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { preloadCommonIcons } from './utils/performance/iconOptimizer'
import './utils/performance/loadingRecovery.ts'

// Prevent scroll restoration on page load - setting once globally
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// Create a global resource hint manager
const createResourceHints = () => {
  // Add preload for critical paths
  const criticalPaths = [
    '/assets/index-',  // Main JS bundle prefix
    '/index.css'       // Main CSS
  ];
  
  // Add preload for hero images above the fold
  const criticalImages = [
    '/lovable-uploads/5ff0c1a6-9b40-485d-bb61-6427fd6e304e.png', // Logo
    '/lovable-uploads/5eb0133e-80eb-467d-b52a-490f125d775c.png'  // AI Avatar
  ];
  
  // Mark LCP elements for the browser
  const markLCPElements = () => {
    // Using requestIdleCallback to avoid blocking the main thread
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => {
        try {
          // Find and mark the hero heading as an important LCP element
          const heroHeading = document.getElementById('hero-heading');
          if (heroHeading) {
            // Set attributes that help browsers prioritize this element
            heroHeading.setAttribute('fetchPriority', 'high');
            heroHeading.setAttribute('importance', 'high');
          }
          
          // Add inline critical CSS for above-the-fold content
          const injectCriticalCSS = () => {
            // This would normally be extracted from main CSS, but for simplicity 
            // we're adding some basic critical CSS here
            const criticalCSS = `
              body {opacity: 1 !important;}
              #hero-heading {visibility: visible !important;}
              .container {display: block !important;}
            `;
            
            const style = document.createElement('style');
            style.textContent = criticalCSS;
            document.head.appendChild(style);
          };
          
          injectCriticalCSS();
        } catch (e) {
          // Silent fail
        }
      });
    }
  };
  
  // Get first few resources loaded
  if (document.currentScript) {
    // Type assertion to ensure script is HTMLScriptElement
    const scriptElement = document.currentScript as HTMLScriptElement;
    if (scriptElement.src) {
      const scriptPath = new URL(scriptElement.src).pathname;
      const assetPath = scriptPath.substring(0, scriptPath.lastIndexOf('/'));
      criticalPaths[0] = assetPath + criticalPaths[0];
    }
  }
  
  // Create a fragment to minimize reflows
  const fragment = document.createDocumentFragment();
  
  // Add preloads for critical paths
  criticalPaths.forEach(path => {
    const hint = document.createElement('link');
    hint.rel = 'preload';
    hint.as = path.includes('.css') ? 'style' : 'script';
    hint.href = path;
    fragment.appendChild(hint);
  });
  
  // Add preloads for critical images if not already in HTML
  criticalImages.forEach(imagePath => {
    // Only add if not already preloaded in HTML
    if (!document.querySelector(`link[rel="preload"][href="${imagePath}"]`)) {
      const hint = document.createElement('link');
      hint.rel = 'preload';
      hint.as = 'image';
      hint.href = imagePath;
      hint.type = 'image/png';
      hint.setAttribute('fetchpriority', 'high');
      fragment.appendChild(hint);
    }
  });
  
  // Add the hints to the document head
  document.head.appendChild(fragment);
  
  // Mark LCP elements after DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markLCPElements);
  } else {
    markLCPElements();
  }
};

// Execute resource hints early but non-blocking
createResourceHints();

// Lower priority icon preloading with idle callback
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => preloadCommonIcons(), { timeout: 2000 });
} else {
  setTimeout(() => preloadCommonIcons(), 2000);
}

// Conditionally load non-critical styles after paint
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  // Load immediately if page is already interactive
  setTimeout(() => {
    import('./components/investor-directory/InvestorDialogStyles.css')
  }, 200);
} else {
  // Defer loading of non-critical styles
  window.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      import('./components/investor-directory/InvestorDialogStyles.css')
    }, 500)
  });
}

// Helper function to dynamically load scripts with delay for non-critical resources
const loadScriptAsync = (src: string, delay: number = 3000, attributes: Record<string, string> = {}) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      
      // Add any additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
      
      script.onload = () => resolve(true);
      script.onerror = (error) => reject(error);
      
      document.body.appendChild(script);
    }, delay);
  });
};

// Defer all third-party scripts using idle callback or long timeout
const loadThirdPartyScripts = () => {
  // Set longer delays for mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const mobileDelay = isMobile ? 5000 : 3000;
  
  // Example of how to load third-party scripts with proper delays
  // loadScriptAsync('https://www.googletagmanager.com/gtag/js?id=G-J1T5N9G5Z7', mobileDelay);
  // loadScriptAsync('https://cdn.jsdelivr.net/npm/crisp-sdk@1.0.0/index.min.js', mobileDelay + 1000);
};

// Wait for page to be interactive before loading non-critical scripts
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => {
    loadThirdPartyScripts();
  }, { timeout: 3000 });
} else {
  // Fallback for browsers that don't support requestIdleCallback
  setTimeout(loadThirdPartyScripts, 3000);
}

// Initialize React application with hydration metadata for better performance
const root = ReactDOM.createRoot(document.getElementById('root')!);

// Render the app after a brief delay on mobile to allow browser to handle critical resources first
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
if (isMobile) {
  // On mobile, give a tiny delay to handle critical paint first
  setTimeout(() => {
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }, 10);
} else {
  // On desktop, render immediately
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Send performance metrics to the console for debugging
if (process.env.NODE_ENV !== 'production') {
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (performance && performance.getEntriesByType) {
        const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        console.log('Performance metrics:', {
          ttfb: Math.round(navEntry.responseStart - navEntry.requestStart),
          fcp: Math.round(performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0),
          lcp: Math.round(performance.getEntriesByName('largest-contentful-paint')[0]?.startTime || 0),
          dcl: Math.round(navEntry.domContentLoadedEventEnd),
          load: Math.round(navEntry.loadEventEnd)
        });
      }
    }, 0);
  });
}

// Add cache response headers optimization hint if on production build
if (process.env.NODE_ENV === 'production') {
  // Tell service workers and Cloudflare to cache assets if available
  navigator.serviceWorker?.getRegistrations?.()
    .then(registrations => {
      if (registrations.length === 0) {
        console.info('No service worker found to handle caching')
      }
    })
    .catch(() => {
      // Silently fail if service worker API not supported
    })
}
